import React, { useState, useEffect } from 'react';
import { Dropdown, Menu } from 'antd';
import { ShareAltOutlined, AppstoreOutlined, TeamOutlined, RightOutlined } from '@ant-design/icons';
import MyHeader from './Header.module.css';
import { useDocument } from '../../Pages/SpreadsheetContainer/documentInfoContex';
import { useOfflineToggle } from '../OfflineToggle/useOfflineToggle';
import { useDarkMode } from '../DarkModeToggle/useDarkMode';
import { ShareModal } from '../ShareModal/ShareModal';
import DocumentMembers from '../Members/members';
import { apps, openApp } from '../AppLauncher/apps';

/**
 * Compact hamburger menu for the header on small screens, after milepad's:
 * it carries what the right-hand controls offer on desktop — Online /
 * Offline, Members, Share, the theme toggle and the app launcher — as rows.
 * Owns its own open state and reads the document from context.
 */
const HeaderMenu = () => {
    const { documentInfo, userInfo, isReadOnly, isTemplateDocument } = useDocument();
    const [open, setOpen] = useState(false);
    // The menu is a portal with its own state; when the viewport grows past
    // the breakpoint the hamburger is hidden by CSS but the open menu would
    // stay behind. Close it as the breakpoint is crossed.
    useEffect(() => {
        const compact = window.matchMedia('(max-width: 767px)');
        const onChange = (e) => {
            if (!e.matches) setOpen(false);
        };
        if (compact.addEventListener) compact.addEventListener('change', onChange);
        else compact.addListener(onChange);
        return () => {
            if (compact.removeEventListener) compact.removeEventListener('change', onChange);
            else compact.removeListener(onChange);
        };
    }, []);
    const { offline, isBrowserOnline, toggle: toggleOffline } = useOfflineToggle();
    const { dark, toggle: toggleDark } = useDarkMode();
    const members = (documentInfo?.members || []).filter((m) => m.id !== userInfo?.userId);
    const canShare = documentInfo?.createBy == userInfo?.userId && !isTemplateDocument;

    const row = (icon, label, extra) => (
        <div className={MyHeader.menuItemContent}>
            <span className={MyHeader.menuIconBox}>{icon}</span>
            <span className={MyHeader.menuIconLabel}>{label}</span>
            {extra}
        </div>
    );
    // Submenu rows draw their own chevron; antd's is hidden in the CSS.
    const arrow = <RightOutlined className={MyHeader.menuArrow} />;

    const menu = (
        <Menu className={MyHeader.compactMenu}>
            {!isReadOnly && !isTemplateDocument && (
                <Menu.Item
                    key="offline"
                    disabled={!isBrowserOnline}
                    onClick={() => { toggleOffline(); setOpen(false); }}
                >
                    <div className={MyHeader.menuItemContent}>
                        <span className={`${MyHeader.menuIconBox} ${offline ? MyHeader.iconBoxAmber : MyHeader.iconBoxGreen}`}>
                            <span className={`${MyHeader.statusDot} ${offline ? MyHeader.statusDotOffline : MyHeader.statusDotOnline}`} />
                        </span>
                        <span className={MyHeader.menuIconLabel}>{offline ? 'Offline' : 'Online'}</span>
                        <span className={`${MyHeader.statusPill} ${offline ? MyHeader.statusPillOffline : MyHeader.statusPillOnline}`}>
                            {offline ? 'Working offline' : 'Synced'}
                        </span>
                    </div>
                </Menu.Item>
            )}
            {members.length > 0 && !isTemplateDocument && (
                <Menu.SubMenu
                    key="members"
                    popupClassName={MyHeader.membersSubPopup}
                    title={row(<TeamOutlined />, 'Members', <><span className={MyHeader.menuCount}>{members.length}</span>{arrow}</>)}
                >
                    <Menu.Item key="members-list" disabled className={MyHeader.membersPreview}>
                        <DocumentMembers />
                    </Menu.Item>
                </Menu.SubMenu>
            )}
            {canShare && (
                <Menu.Item key="share">
                    <ShareModal
                        viewMode="menu"
                        onMenuClick={() => setOpen(false)}
                        menuRow={row(<ShareAltOutlined />, 'Share')}
                    />
                </Menu.Item>
            )}
            <Menu.Divider className={MyHeader.menuDivider} />
            <Menu.Item key="mode" onClick={() => { toggleDark(); setOpen(false); }}>
                {row(<span className={MyHeader.menuModeIcon} aria-hidden="true">{dark ? '☀' : '☾'}</span>, dark ? 'Light mode' : 'Dark mode')}
            </Menu.Item>
            <Menu.SubMenu key="apps" popupClassName={MyHeader.appsSubPopup} title={row(<AppstoreOutlined />, 'Apps', arrow)}>
                {apps.map((app) => (
                    <Menu.Item key={app.id} onClick={() => { openApp(app.url); setOpen(false); }}>
                        <div className={MyHeader.menuItemContent}>
                            <span className={MyHeader.menuIconBox}>
                                <img src={app.icon} alt="" className={MyHeader.menuIcon} />
                            </span>
                            <span className={MyHeader.menuIconLabel}>{app.name}</span>
                        </div>
                    </Menu.Item>
                ))}
            </Menu.SubMenu>
        </Menu>
    );

    return (
        <Dropdown
            overlay={menu}
            trigger={['click']}
            visible={open}
            onVisibleChange={setOpen}
            placement="bottomRight"
        >
            <button
                type="button"
                className={`${MyHeader.hamburgerBtn} ${open ? MyHeader.hamburgerBtnActive : ''}`}
                aria-label="Menu"
                aria-expanded={open}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                    <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
            </button>
        </Dropdown>
    );
};

export default HeaderMenu;
